import React from "react";
import Layout from "../PageLayout/Layout";
import TrustStrip from "../shared/TrustStrip";
import "./ProductInsurance.css";

const TRUST_ICONS = {
  shield: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 3l7 3v5.5c0 4.4-2.9 8.4-7 9.5-4.1-1.1-7-5.1-7-9.5V6l7-3z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  ),
  clock: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="8.25" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M12 8v4l2.5 2.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  user: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="8" r="3.25" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M5.5 19.5c.8-3 3.2-4.75 6.5-4.75"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <circle cx="17.5" cy="16.5" r="2.75" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M16.6 16.5h1.8M17.5 15.6v1.8"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  ),
  bulb: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M9.5 17.5h5M10.2 20h3.6M12 3.75a5.75 5.75 0 0 1 3.4 10.4c-.7.55-1.1 1.25-1.2 2.1h-4.4c-.1-.85-.5-1.55-1.2-2.1A5.75 5.75 0 0 1 12 3.75z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
};

const TRUST_ITEMS = [
  {
    icon: TRUST_ICONS.shield,
    title: "Security by design",
    text: "Compliance and access control are embedded at the architecture level, not added later.",
  },
  {
    icon: TRUST_ICONS.clock,
    title: "Full audit trail",
    text: "Every AI decision and human override is logged with timestamp, user, and justification.",
  },
  {
    icon: TRUST_ICONS.user,
    title: "Role-based access",
    text: "Underwriters, adjusters, managers, and auditors each see exactly what their role permits.",
  },
  {
    icon: TRUST_ICONS.bulb,
    title: "Explainable by default",
    text: "Every AI recommendation shows its evidence, confidence, and the rules that produced it.",
  },
];

const WORKFLOW_BLOCKS = [
  {
    id: "answers",
    title: "Get the right answer, every time — securely, compliantly, and in context.",
    body: "Highwatch connects every enterprise system, from Salesforce to SharePoint, and understands your organization's language, policies, and workflows. Ask a question, and Corelink delivers the exact, verified answer in seconds — not a guess, but a fully traceable, compliant response you can act on immediately.",
    bullets: [
      "Finds precise answers across all connected systems and documents",
      "Understands company-specific context, rules, and compliance policies",
      "Returns verified, citation-backed responses in under two seconds",
    ],
    image: "/images/Overlay+Border+Shadow.svg",
    alt: "Insights dashboard",
    reverse: false,
  },
  {
    id: "workflows",
    title: "Highwatch doesn't just respond, it executes workflows end-to-end.",
    body: "Beyond search, Highwatch automates what happens next. It generates reports, routes approvals, updates records, and triggers downstream workflows — all within your enterprise environment. Every action is logged, governed, and auditable, ensuring accountability and compliance from start to finish.",
    bullets: [
      "Automates workflows across CRMs, ERPs, and collaboration tools",
      "Maintains full traceability and audit logs for every action",
      "Cuts workflow completion time by up to 70–75%",
    ],
    image: "/images/Overlay+Border+Shadow (1).svg",
    alt: "Infrastructure dashboard",
    reverse: true,
  },
  {
    id: "integrations",
    title: "Every integration, monitored in one place.",
    body: "Highwatch plugs into Salesforce, HubSpot, SAP, Slack, and the rest of your stack — giving you one view of connector health, sync status, and data freshness without disrupting how your teams already work.",
    bullets: [
      "Live health status for every connector, updated in real time",
      "Adjustable sync frequency — hourly, daily, weekly, or manual",
      "One-click retry the moment a connection fails",
    ],
    image: "/images/Overlay+Border+Shadow (2).svg",
    alt: "Integrations dashboard",
    reverse: false,
  },
];

export default function RealWorkflow() {
  return (
    <>
      {/* Mobile — unchanged */}
      <section className="py-20 pi-mobile">
        <Layout>
          <div>
            <div className="text-center max-w-3xl mx-auto mb-16">
              <div className="internal-hero-badge flex items-center gap-2 mb-6">
                <img src="/icons/bolt.svg" alt="bolt Icon" className="w-5 h-5" />
                <span>Real Workflow Examples</span>
              </div>

              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
                Where Enterprise Knowledge Becomes Action
              </h2>
              <p className="mt-4 text-gray-500 text-base">
                Highwatch connects every system, understands your workflows, and executes the next step, all with compliance built in.
              </p>
            </div>

            <div className="flex flex-col lg:flex-row items-stretch gap-10 mb-20">
              <div className="lg:w-1/2 w-full h-full flex flex-col justify-start">
                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                    Get the right answer, every time — securely, compliantly, and in context.
                  </h3>
                  <p className="text-gray-500 mb-6">
                    Highwatch connects every enterprise system, from Salesforce to SharePoint, and understands your organization’s language, policies, and workflows.
                    Ask a question, and Corelink delivers the exact, verified answer in seconds, not a guess, but a fully traceable, compliant response you can act on immediately.
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3"><span className="inline-block mt-1 text-green-500">✅</span><p className="text-gray-700">Finds precise answers across all connected systems and documents</p></li>
                    <li className="flex items-start gap-3"><span className="inline-block mt-1 text-green-500">✅</span><p className="text-gray-700">Understands company-specific context, rules, and compliance policies</p></li>
                    <li className="flex items-start gap-3"><span className="inline-block mt-1 text-green-500">✅</span><p className="text-gray-700">Returns verified, citation-backed responses in under two seconds</p></li>
                  </ul>
                </div>
              </div>
              <div className="lg:w-1/2 w-full h-full">
                <img
                  src="/images/highwatchprofile.png"
                  alt="Workflow preview 1"
                  className="rounded-xl shadow-md w-full h-full object-cover"
                />
              </div>
            </div>

            <div className="flex flex-col-reverse lg:flex-row items-stretch gap-10 mb-20">
              <div className="lg:w-1/2 w-full h-full">
                <img
                  src="/images/highwatchprofile.png"
                  alt="Workflow preview 2"
                  className="rounded-xl shadow-md w-full h-full object-cover"
                />
              </div>
              <div className="lg:w-1/2 w-full h-full flex flex-col justify-start">
                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                    Highwatch doesn’t just respond, it executes workflows end-to-end.
                  </h3>
                  <p className="text-gray-500 mb-6">
                    Beyond search, Highwatch automates what happens next. It generates reports, routes approvals, updates records, and triggers downstream workflows, all within your enterprise environment.
                    Every action is logged, governed, and auditable, ensuring accountability and compliance from start to finish.
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3"><span className="inline-block mt-1 text-green-500">✅</span><p className="text-gray-700">Automates workflows across CRMs, ERPs, and collaboration tools</p></li>
                    <li className="flex items-start gap-3"><span className="inline-block mt-1 text-green-500">✅</span><p className="text-gray-700">Maintains full traceability and audit logs for every action</p></li>
                    <li className="flex items-start gap-3"><span className="inline-block mt-1 text-green-500">✅</span><p className="text-gray-700">Cuts workflow completion time by up to 70–75%</p></li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="flex flex-col lg:flex-row items-stretch gap-10 lg:-mt-4">
              <div className="lg:w-1/2 w-full h-full flex flex-col justify-start">
                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                    Your organization’s knowledge, unified and always up to date.
                  </h3>
                  <p className="text-gray-500 mb-6">
                    Highwatch continuously syncs knowledge across systems, policies, procedures, customer data, and compliance records, into a single, secure intelligence layer.
                    No silos, no outdated files. Every team sees the same truth, instantly, with enterprise-grade security built in.
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3"><span className="inline-block mt-1 text-green-500">✅</span><p className="text-gray-700">Real-time sync across all knowledge systems and data sources</p></li>
                    <li className="flex items-start gap-3"><span className="inline-block mt-1 text-green-500">✅</span><p className="text-gray-700">Redaction and access controls ensure data privacy and compliance</p></li>
                    <li className="flex items-start gap-3"><span className="inline-block mt-1 text-green-500">✅</span><p className="text-gray-700">Continuous learning improves accuracy and personalization over time</p></li>
                  </ul>
                </div>
              </div>
              <div className="lg:w-1/2 w-full h-full">
                <img
                  src="/images/highwatchprofile.png"
                  alt="Workflow preview 3"
                  className="rounded-xl shadow-md w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </Layout>
      </section>

      {/* Desktop — Product Intelligence workflow examples */}
      <div className="pi-intel-desktop">
        <section className="pi-intel-workflow">
          <div className="pi-intel-workflow__header">
            <p className="pi-eyebrow">REAL WORKFLOW EXAMPLES</p>
            <h2>Where Enterprise Knowledge Becomes Action</h2>
            <p>
              Highwatch connects every system, understands your workflows, and executes the next
              step — all with compliance built in.
            </p>
          </div>
        </section>

        {WORKFLOW_BLOCKS.map((block) => (
          <section
            key={block.id}
            className={`pi-feature pi-feature--${block.id}${block.reverse ? " pi-feature--reverse" : ""}`}
          >
            <div className="pi-feature__inner">
              <div className="pi-feature__copy">
                <h2>{block.title}</h2>
                <p>{block.body}</p>
                <ul>
                  {block.bullets.map((item) => (
                    <li key={item}>
                      <span>✓</span> {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="pi-feature__visual">
                <img
                  src={block.image}
                  alt={block.alt}
                  className="pi-intel-feature__image"
                />
              </div>
            </div>
          </section>
        ))}

        <TrustStrip />
      </div>

      {/* Desktop — feature sections */}
      <section id="pi-submission" className="pi-desktop pi-feature pi-feature--submission">
        <div className="pi-feature__inner">
          <div className="pi-feature__copy">
            <span className="pi-pill pi-pill--teal">SUBMISSION INTELLIGENCE</span>
            <h2>One queue. Every submission, prioritized by what matters.</h2>
            <p>
              Highwatch reads incoming submissions, extracts the data underwriters need, and
              surfaces risk and SLA urgency in a single operational view — so nothing falls
              through the cracks.
            </p>
            <ul>
              <li><span>✓</span> AI-assisted document parsing and field extraction</li>
              <li><span>✓</span> Risk scoring with full evidence trail</li>
              <li><span>✓</span> SLA-aware prioritization, automatically</li>
            </ul>
          </div>
          <div className="pi-feature__visual">
            <img
              src="/images/SubmissionOverview.svg"
              alt="Submission Intelligence dashboard"
              className="pi-feature__image"
            />
          </div>
        </div>
      </section>

      <section className="pi-desktop pi-feature pi-feature--reverse pi-feature--decision">
        <div className="pi-feature__inner">
          <div className="pi-feature__copy">
            <span className="pi-pill pi-pill--blue">DECISION SUPPORT</span>
            <h2>Every AI recommendation comes with its reasoning.</h2>
            <p>
              Highwatch shows evidence, rules, and confidence levels so underwriters can approve,
              refer, or override with full context — and a complete audit trail.
            </p>
            <ul>
              <li><span>✓</span> Approve, decline, or refer with full context</li>
              <li><span>✓</span> Override with a required, auditable justification</li>
              <li><span>✓</span> Confidence scoring on every recommendation</li>
            </ul>
          </div>
          <div className="pi-feature__visual">
            <img
              src="/images/SubmissionDecision.svg"
              alt="Decision Support dashboard"
              className="pi-feature__image"
            />
          </div>
        </div>
      </section>

      <section className="pi-desktop pi-feature pi-feature--fraud">
        <div className="pi-feature__inner">
          <div className="pi-feature__copy">
            <span className="pi-pill pi-pill--pink">FRAUD & RISK</span>
            <h2>Catch what manual review misses.</h2>
            <p>
              Highwatch continuously scores claims for fraud, severity, leakage, and complexity —
              flagging the cases that need investigation before payout, not after.
            </p>
            <ul>
              <li><span>✓</span> Real-time fraud and severity scoring</li>
              <li><span>✓</span> Dedicated investigation workspace</li>
              <li><span>✓</span> Every flag traceable to its source signal</li>
            </ul>
          </div>
          <div className="pi-feature__visual">
            <img
              src="/images/FraudWorkspace.svg"
              alt="Fraud Workspace dashboard"
              className="pi-feature__image"
            />
          </div>
        </div>
      </section>

      <TrustStrip className="pi-desktop" />

      <section className="pi-desktop pi-trust">
        <div className="pi-trust__inner">
          <div className="pi-trust__copy">
            <p className="pi-eyebrow">TRUST & COMPLIANCE</p>
            <h2>Enterprise infrastructure is judged by how it handles trust — not just speed.</h2>
            <p>Highwatch is designed for regulated organizations from the ground up.</p>
            <p>
              Every decision is explainable, every action is logged, and every override requires a
              documented reason.
            </p>
          </div>
          <div className="pi-trust__list">
            {TRUST_ITEMS.map((item) => (
              <article key={item.title} className="pi-trust__item">
                <span className="pi-trust__icon" aria-hidden="true">
                  {item.icon}
                </span>
                <div>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
